import $ from 'jquery';
import React, { useContext } from 'react';

import { getInstrumentation as getInstrumentationInstance } from '@bingads-webui-campaign/instrumentation-setup';
import { isInternalUrl } from '@bingads-webui/url-util';
import { AjaxStub, ErrorStub } from '@bingads-webui/instrumentation-stubs';
import { OdataErrorMapper } from '@bingads-webui-campaign/instrumentation';
import { createErrorMessage } from '@bingads-webui-campaign/error-utilities';

import { DefaultGlobalErrorView } from './global-error-view';
import { globalErrorSchema, instrumentationSchemas } from './instrumentation-schemas';
import { expectedErrorCodes } from './expected-error-codes';
import { AjaxEventHandlerWithSignOutRedirect } from './ajax-event-handler';
import { InstrumentationContext } from './instrumentation-context';

export const SUB_SYSTEM_NAMES = {
  UP: 'up',
  MCA: 'mca',
  UpExpert: 'up-expert',
  SubAdsLanding: 'subAdsLanding',
  UpLandingPage: 'upLandingPage',
};

function getErrorMappers() {
  const odataErrorMapper = new OdataErrorMapper(expectedErrorCodes);
  return {
    odataErrorMapper,
  };
}

function stub(instrumentation, logger, serverContext) {
  const stubOptions = {
    objectsToStub: [$],
    pageTrackingId: serverContext && serverContext.PageTrackingId,
    lcid: serverContext && serverContext.CurrentUser && serverContext.CurrentUser.Locale,
  };

  const errorMappers = getErrorMappers();
  const shouldAddHeaders = ajaxContext => isInternalUrl(ajaxContext.url);
  const eventHandler = new AjaxEventHandlerWithSignOutRedirect({
    errorMappers,
    shouldAddHeaders,
    serverContext,
    logger,
  });

  const ajaxStub = new AjaxStub({
    stubOptions,
    eventHandler,
    instrumentation,
  });

  const errorStub = new ErrorStub({
    instrumentation,
  });

  ajaxStub.init();
  errorStub.init();
}

export const getGlobalErrorOptions = ({ instrumentation, isUnifiedProduct }) => ({
  logError: ({ error, info }) => {
    if (instrumentation) {
      instrumentation.addScenario(globalErrorSchema);
      const appReactGlobalError = instrumentation.appReactGlobalError.create();
      const logError = appReactGlobalError.logError.create();
      logError.error(createErrorMessage(error));
      logError.error(info.componentStack);
    }
  },
  renderErrorView: () =>
    (<DefaultGlobalErrorView
      isUnifiedProduct={isUnifiedProduct}
    />),
});

export const getInstrumentationService = ({ instrumentation, serverContext }) => {
  instrumentation.addScenario(instrumentationSchemas);
  const defaultScenario = instrumentation.defaultScenario.create();
  const logger = defaultScenario.logger.create();

  stub(instrumentation, logger, serverContext);

  return {
    instrumentation,
    logger,
  };
};

export const getInstrumentation = ({
  serverContext,
  devConfig,
  subSystemName,
}) => {
  const {
    CurrentCustomer,
    CurrentAccount,
    CurrentUser,
  } = serverContext;

  const userContext = {
    CurrentCustomer: CurrentCustomer || {},
    CurrentAccount: CurrentAccount || {},
    CurrentUser: CurrentUser || {},
  };

  return getInstrumentationInstance({
    pageTrackingId: serverContext.PageTrackingId,
    errorMappers: getErrorMappers(),
    isInDevelopmentMode: devConfig.isInDevelopmentMode,
    userContext,
    applicationName: {
      systemName: 'campaign',
      subSystemName,
    },
    serverContext: {
      // Ui Next is using dynamic permissions config
      // We don't have that in ESC sign up bundle context. Enable it for all users to expose errors.
      DynamicPermissions: {
        EnableLoggingForUnhandledPromiseRejections: true,
      },
      ...serverContext,
      ...userContext,
    },
    objectsToStub: [],
    ariaConfig: subSystemName === SUB_SYSTEM_NAMES.UP ? {
      flushInterval: 1000,
      batchSize: 80,
    } : {},
  });
};

export const withGlobalErrorOptions = (Component, isUnifiedProduct) => {
  const Wrapper = React.memo((props) => {
    const [instrumentationContext] = useContext(InstrumentationContext);
    return (
      <Component
        globalErrorOptions={getGlobalErrorOptions({
          instrumentation: instrumentationContext ? instrumentationContext.instrumentation : null,
          isUnifiedProduct,
        })}
        {...props}
      />
    );
  });

  return Wrapper;
};
